// src/components/global-content/ContentCard.js - Individual content item card
import React, { useState } from 'react';
import PropTypes from 'prop-types';

const ContentCard = ({ content, onEdit, onArchive, onFetchContent, formatDate, getContentTypeInfo }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [fullContent, setFullContent] = useState(null);

  const typeInfo = getContentTypeInfo(content.contentType);
  
  const handlePreviewToggle = async () => {
    if (!showPreview) {
      // Need to fetch full content for preview
      if (!fullContent && !content.content) {
        setIsLoadingPreview(true);
        try {
          const fetchedContent = await onFetchContent(content.key);
          setFullContent(fetchedContent);
        } catch (err) {
          console.error('Error loading content preview:', err);
        } finally {
          setIsLoadingPreview(false);
        }
      }
      setShowPreview(true);
    } else {
      setShowPreview(false);
    }
  };
  
  const handleArchive = async () => {
    setIsArchiving(true);
    try {
      await onArchive();
    } catch (err) {
      console.error('Archive failed:', err);
    } finally {
      setIsArchiving(false);
    }
  };

  const getPreviewText = (text, maxLength = 200) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getStatusBadge = () => {
    if (!content.isActive) {
      return <span className="status-pill archived">Archived</span>;
    }
    return <span className="status-pill active">Active</span>;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'prompt':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        );
      case 'instruction':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      case 'context':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        );
      case 'example':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'output_format':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
    }
  };

  return (
    <div className="content-card">
      {/* Mobile View */}
      <div className="content-card-mobile md:hidden">
        <div className="content-card-header">
          <div className="flex gap-3 items-start">
            <div className="content-type-icon">
              {getTypeIcon(content.contentType)}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="content-title">{content.title}</h3>
              <div className="content-meta">
                <span className="content-key">Key: {content.key}</span>
                <span className="content-type">{typeInfo.label}</span>
                {getStatusBadge()}
              </div>
            </div>
          </div>
        </div>
        
        {content.description && (
          <p className="content-description">{content.description}</p>
        )}
        
        <div className="content-stats">
          <span>{content.contentLength || 0} chars</span>
          <span>{content.wordCount || 0} words</span>
          <span>v{content.version || 1}</span>
          <span>Updated {formatDate(content.updatedAt)}</span>
        </div>

        <div className="content-actions">
          <button
            onClick={handlePreviewToggle}
            className="btn btn-sm btn-secondary"
            disabled={isLoadingPreview}
          >
            {isLoadingPreview ? (
              <>
                <div className="loading-dots" style={{ scale: '0.5', marginRight: '4px' }}>
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                </div>
                Loading...
              </>
            ) : (
              showPreview ? 'Hide' : 'Preview'
            )}
          </button>
          <button
            onClick={onEdit}
            className="btn btn-sm btn-secondary"
          >
            Edit
          </button>
          <button
            onClick={handleArchive}
            disabled={isArchiving}
            className="btn btn-sm"
            style={{ backgroundColor: '#dc2626', color: 'white' }}
          >
            {isArchiving ? 'Archiving...' : 'Archive'}
          </button>
        </div>
      </div>

      {/* Desktop View */}
      <div className="content-card-desktop">
        <div className="content-main-info">
          <div className="flex gap-3 items-start">
            <div className="content-type-icon">
              {getTypeIcon(content.contentType)}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="content-title">{content.title}</h3>
              <div className="content-key">Key: {content.key}</div>
              {content.description && (
                <p className="content-description">{content.description}</p>
              )}
            </div>
          </div>
        </div>

        <div className="content-card-right-panel">
          <div className="content-type-info">
            <span className="content-type-badge">{typeInfo.label}</span>
            {getStatusBadge()}
          </div>

          <div className="content-date-info">
            <div>Updated {formatDate(content.updatedAt)}</div>
            <div className="content-stats-inline">
              {content.contentLength || 0} chars • {content.wordCount || 0} words • v{content.version || 1}
            </div>
          </div>

          <div className="content-actions">
            <button
              onClick={handlePreviewToggle}
              className="btn btn-sm btn-secondary"
              disabled={isLoadingPreview}
            >
              {isLoadingPreview ? 'Loading...' : showPreview ? 'Hide' : 'Preview'}
            </button>
            <button
              onClick={onEdit}
              className="btn btn-sm btn-secondary"
            >
              Edit
            </button>
          </div>
        </div>
      </div>

      {/* Content Preview */}
      {showPreview && (
        <div className="content-preview">
          <div className="content-preview-header">
            <span className="text-sm font-medium text-gray-700">Content Preview</span>
            <button
              onClick={() => setShowPreview(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="content-preview-text">
            {isLoadingPreview ? (
              <div className="flex items-center justify-center py-8">
                <div className="loading-dots">
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                </div>
                <span className="ml-2 text-gray-600">Loading content...</span>
              </div>
            ) : (
              (fullContent?.content || content.content) ? 
                getPreviewText(fullContent?.content || content.content, 500) : 
                'No content available'
            )}
          </div>
          {(fullContent?.metadata || content.metadata) && Object.keys(fullContent?.metadata || content.metadata).length > 0 && (
            <div className="content-metadata">
              <span className="text-sm font-medium text-gray-700">Metadata:</span>
              <pre className="text-xs text-gray-600 mt-1">
                {JSON.stringify(fullContent?.metadata || content.metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ContentCard.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    content: PropTypes.string,
    contentType: PropTypes.string.isRequired,
    contentLength: PropTypes.number,
    wordCount: PropTypes.number,
    version: PropTypes.number,
    isActive: PropTypes.bool,
    updatedAt: PropTypes.string,
    metadata: PropTypes.object
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onFetchContent: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
  getContentTypeInfo: PropTypes.func.isRequired
};

export default ContentCard;